import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
	ButtonStandard,
	FormHandle,
	FormInputText,
	FormTextEditor,
	ModalBase,
	PYForm,
	Text,
	toast,
	usePyForm,
} from "@performyard/py-components";
import { MAX_ROLE_DESCRIPTION_LENGTH } from "@performyard/service-contracts";
import type { Role } from "@performyard/service-contracts";

import { clearFieldServerError, setFieldServerError } from "../../../../components/PYFormBridges/setFieldServerError";

import { useUpdateRole } from "../../../../api/hooks/defs/roles";

import { type RoleDetailsFormValues, roleDetailsSchema } from "../CreateEditRole/validationSchema";

const NAME_COLLISION_MESSAGE = "Role name already exists";

export interface RoleDetailsModalProps {
	isOpen: boolean;
	role: Role;
	onClose: () => void;
}

interface NameErrorBridgeHandle {
	setNameError: (message: string) => void;
	clearNameError: () => void;
}

// PYForm's onSubmit `setError` only writes `serverError`, which react-query's async catch runs
// too late for FormField to pick up — same fix as CreateEditRole/RoleForm.tsx's name collision.
const NameErrorBridge = forwardRef<NameErrorBridgeHandle>((_, ref) => {
	const pyForm = usePyForm();
	useImperativeHandle(
		ref,
		() => ({
			setNameError: (message: string) => setFieldServerError(pyForm, "name", message),
			clearNameError: () => clearFieldServerError(pyForm, "name"),
		}),
		[pyForm]
	);
	return null;
});
NameErrorBridge.displayName = "NameErrorBridge";

const RoleDetailsModal: React.FC<RoleDetailsModalProps> = ({ isOpen, role, onClose }) => {
	const formRef = useRef<FormHandle<typeof roleDetailsSchema>>(null);
	const nameErrorRef = useRef<NameErrorBridgeHandle>(null);
	const [apiError, setApiError] = useState<string | null>(null);

	const { mutateAsync, isPending } = useUpdateRole();

	useEffect(() => {
		if (isOpen) {
			setApiError(null);
			formRef.current?.reset();
		}
	}, [isOpen, role._id]);

	const defaultValues: RoleDetailsFormValues = {
		name: role.name,
		description: role.description ?? "",
	};

	const handleSubmit = async ({ value }: { value: RoleDetailsFormValues }) => {
		setApiError(null);
		try {
			// Name + description ONLY — never competencies. RoleService.update rewrites the stored
			// array wholesale without cascading expected levels, so omitting the field is what keeps
			// existing holder assignments untouched.
			await mutateAsync({ id: role._id, body: { name: value.name, description: value.description } });
			toast.success("Changes saved");
			onClose();
		} catch (error) {
			const err = error as { response?: { status?: number }; data?: { message?: string } };
			if (err.response?.status === 409) {
				nameErrorRef.current?.setNameError(NAME_COLLISION_MESSAGE);
			} else {
				setApiError(err.data?.message || "Something went wrong");
			}
		}
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose}>
			<div className="w-[432px] px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="mb-6 text-center">
					Edit Role Details
				</Text>

				<PYForm
					key={role._id}
					ref={formRef}
					id="role-details-form"
					schema={roleDetailsSchema}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					validateOn={["onSubmit"]}
				>
					<NameErrorBridge ref={nameErrorRef} />
					<div className="flex flex-col gap-4">
						<FormInputText id="name" label="Role Name" placeholder="Your text here" />

						<FormTextEditor
							id="description"
							label="Description"
							characterLimit={MAX_ROLE_DESCRIPTION_LENGTH}
							placeholder="Description"
							minHeight={60}
							maxHeight={120}
						/>

						{apiError && (
							<Text size="caption" color="red-5">
								{apiError}
							</Text>
						)}

						<div className="mt-2 flex justify-center gap-2">
							<div>
								<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
									Cancel
								</ButtonStandard>
							</div>
							<div>
								<ButtonStandard
									variant="solid"
									color="primary"
									size="large"
									type="button"
									disabled={isPending}
									onClick={async () => {
										// A previous collision's server error isn't a schema key, so validation
										// won't clear it — leave it and this gate blocks every later retry.
										nameErrorRef.current?.clearNameError();
										const isValid = await formRef.current?.validate(true);
										if (isValid) {
											await formRef.current?.submit();
										}
									}}
								>
									Save Changes
								</ButtonStandard>
							</div>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default RoleDetailsModal;
